<template lang="pug">
.main-tab
    el-form-item(:label="$t('Value')" :prop="propType.value_field")
        .flex-row
            .text-field.grow(v-if="['text', 'color'].includes(propType.key)")
                .translation-origin(
                    v-if="activeTranslation && valueOption.translationOrigin.text"
                    :class="{ visible: showTranslationOrigin.includes('text') }"
                )
                    button.language(type="button" @click="toggleTranslationOriginVisibility('text')")
                        span(v-if="showTranslationOrigin") {{ getLanguageName(defaultLanguage.code) }}
                        span(v-else) {{ $t('Show original text') }}
                        span.circle.fi.fis(:class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`")
                    transition(name="show-translation")
                        .translation-origin-content(
                            v-if="showTranslationOrigin.includes('text')"
                            v-html="valueOption.translationOrigin.text"
                        )
                el-input(
                    v-model="value"
                    :maxlength="maxLength"
                    show-word-limit
                    :disabled="activeTranslation && !valueIsTranslatable"
                )
            .number-field.grow(v-else-if="['number', 'unit'].includes(propType.key)")
                el-input(
                    v-model="value"
                    type="number"
                    :disabled="activeTranslation"
                )
            .date-field.grow(v-else-if="propType.key === 'date'")
                el-date-picker.is-full-width(
                    v-model="date"
                    :type="property.datetype"
                    :format="dateFormat"
                    prefix-icon="far el-icon-fa-calendar-alt"
                    :picker-options="{ firstDayOfWeek:1 }"
                    :disabled="activeTranslation"
                )
            el-color-picker(v-if="!activeTranslation && propType.key === 'color'" v-model="valueAlt")
    el-form-item.value-option-key(v-if="!activeTranslation" :label="$t('Key')" prop="key")
        el-input(v-model="key" maxlength="180" show-word-limit)
        .help {{ $t('Identifies this option in imports and keeps the articles attached to it. It stays put when the value is renamed.') }}
    el-form-item(v-if="!activeTranslation" :label="$t('Image')" prop="image")
        transition(name="slide-fade" mode="out-in")
            el-upload.plus-box(
                v-if="!valueOption.image || !valueOption.image.source"
                ref="upload"
                drag :action="uploadEndpoint"
                accept="image/jpg, image/jpeg, image/png"
                :before-upload="validateUpload"
                :on-change="updateUploadHeaders"
                :on-success="uploadSucceed"
                :on-error="uploadFailed"
                :headers="uploadHeaders"
                :data="uploadData"
            )
                i.el-icon-plus
                // i.el-icon-upload.el-icon-fa-cloud-upload-alt.fas
                .el-upload__text(v-html="$t('upload.drop.plural')")
                .el-upload__tip(slot="tip") {{ $t('upload.validation', { filetypes: 'jpg/png', size: '2mb' }) }}
            .property-value-image(v-else :class="{ deleted: valueOption.image.deleted }")
                img(
                    :width="valueOption.image.width"
                    :height="valueOption.image.height"
                    :src="valueOption.image.source.url"
                )
                el-button.restore-image(
                    v-if="valueOption.image.deleted"
                    circle type="success" size="mini"
                    icon="el-icon-fa-trash-restore fas"
                    @click="restoreImage"
                )
                el-button.delete-image(
                    v-else
                    circle type="info" size="mini"
                    icon="el-icon-fa-trash-alt far"
                    @click="deleteImage"
                )
    el-form-item(:label="$t('Addition')" prop="addition")
        .translation-origin(
            v-if="activeTranslation && valueOption.translationOrigin.addition"
            :class="{ visible: showTranslationOrigin.includes('addition') }"
        )
            button.language(type="button" @click="toggleTranslationOriginVisibility('addition')")
                span(v-if="showTranslationOrigin") {{ getLanguageName(defaultLanguage.code) }}
                span(v-else) {{ $t('Show original text') }}
                span.circle.fi.fis(:class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`")
            transition(name="show-translation")
                .translation-origin-content(
                    v-if="showTranslationOrigin.includes('addition')"
                    v-html="valueOption.translationOrigin.addition"
                )
        el-input(
            v-model="valueOption.addition"
            maxlength="255"
            show-word-limit
        )
    el-form-item(:label="$t('Description')" prop="description")
        .translation-origin(
            v-if="activeTranslation && valueOption.translationOrigin.description"
            :class="{ visible: showTranslationOrigin.includes('description') }"
        )
            button.language(type="button" @click="toggleTranslationOriginVisibility('description')")
                span(v-if="showTranslationOrigin") {{ getLanguageName(defaultLanguage.code) }}
                span(v-else) {{ $t('Show original text') }}
                span.circle.fi.fis(:class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`")
            transition(name="show-translation")
                .translation-origin-content(
                    v-if="showTranslationOrigin.includes('description')"
                    v-html="valueOption.translationOrigin.description"
                )
        el-input(
            v-model="valueOption.description"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 40 }"
        )

    el-form-item(v-if="valueOptionGroupsFeatureEnabled" :label="$t('Group')" prop="group")
        .translation-origin(
            v-if="activeTranslation && valueOption.translationOrigin.group"
            :class="{ visible: showTranslationOrigin.includes('group') }"
        )
            button.language(type="button" @click="toggleTranslationOriginVisibility('group')")
                span(v-if="showTranslationOrigin") {{ getLanguageName(defaultLanguage.code) }}
                span(v-else) {{ $t('Show original text') }}
                span.circle.fi.fis(:class="`fi-${defaultLanguage.flag || getLanguageFlag(defaultLanguage.code)}`")
            transition(name="show-translation")
                .translation-origin-content(
                    v-if="showTranslationOrigin.includes('group')"
                    v-html="valueOption.translationOrigin.group"
                )
        //- el-input(
        //-     v-model="valueOption.group"
        //-     maxlength="255"
        //-     show-word-limit
        //- )

        el-autocomplete.is-full-width(
            v-model="valueOption.group"
            :hide-loading="true"
            :popper-append-to-body="true"
            :debounce="0"
            :fetch-suggestions="findGroups"
            autocomplete="no-fill"
        )
</template>

<i18n>
{
    "de": {
        "Value": "Wert",
        "Key": "Eindeutige Kennung",
        "Identifies this option in imports and keeps the articles attached to it. It stays put when the value is renamed.": "Identifiziert diese Option in Importen und hält die daran hängenden Artikel fest. Beim Umbenennen des Werts bleibt er unverändert.",
        "Image": "Bild",
        "Addition": "Zusatz",
        "Description": "Beschreibung",
        "Group": "Gruppierung"
    }
}
</i18n>

<script>
import _ from 'lodash';

import Fuse from 'fuse.js';
import slugify from 'slugify';
import uniqid from 'uniqid';
import { useSetupStore } from '@/stores';
import { startOfDay, getUnixTime, fromUnixTime } from 'date-fns';
import { appFromUnixTime, appGetUnixTime } from '@/utils/timezone';

import { ensureFreshToken } from '@/helpers';
import { language, languageFlag, languageNativeName, timestamp2date } from '@/filters';

import store from '@/store';
import { useAuthStore } from '@/stores';


export default {
    name: 'MainTab',
    props: {
        valueOption: {
            type: Object,
            required: true,
        },
        propType: {
            type: Object,
            required: true,
        },
        property: {
            type: Object,
            required: true,
        },
        dateFormat: {
            type: String,
            required: false,
            default: 'yyyy-MM-dd',
        },
    },
    data() {
        return {
            showTranslationOrigin: [],
            keyTouched: false,
            invalidFiles: [], // old / new
        };
    },
    computed: {
        uploadHeaders() {
            return {
                authorization: `Bearer ${useAuthStore().token}`,
                accept: 'application/json',
            }
        },
        key: {
            get() {
                return this.valueOption.key;
            },
            set(key) {
                this.keyTouched = true;
                this.$set(this.valueOption, 'key', key);
            },
        },
        // Text is the only value worth translating. A number, a date or a colour is
        // the same in every language and stays on the origin. Asking the value field
        // rather than the prop type's name is what keeps a colour property - whose
        // value field is text - translatable like any other text.
        valueIsTranslatable() {
            return this.propType.value_field === 'text';
        },
        value: {
            get() {
                if (this.activeTranslation && !this.valueIsTranslatable) {
                    return _.get(this.valueOption.translationOrigin, this.propType.value_field);
                } else {
                    return this.valueOption[this.propType.value_field];
                }
            },
            set(value) {
                if (!this.activeTranslation || this.valueIsTranslatable) {
                    if (this.propType.value_field === 'number') value = parseFloat(value);
                    this.$set(this.valueOption, this.propType.value_field, value);
                }
            },
        },
        valueAlt: {
            get() {
                if (this.activeTranslation) {
                    return _.get(this.valueOption.translationOrigin, this.propType.value_field_alt);
                } else {
                    return this.valueOption[this.propType.value_field_alt];
                }
            },
            set(value) {
                if (!this.activeTranslation) {
                    this.$set(this.valueOption, this.propType.value_field_alt, value);
                }
            },
        },
        date: {
            get() {
                if (this.activeTranslation) {
                    let timestamp = _.get(this.valueOption, 'translationOrigin.date');
                    return timestamp != null ? startOfDay(appFromUnixTime(timestamp)) : '';
                } else {
                    let timestamp = _.get(this.valueOption, 'date');
                    return timestamp != null ? startOfDay(appFromUnixTime(timestamp)) : '';
                }
            },
            set(value) {
                if (!this.activeTranslation) {
                    const timestamp = value != null ? appGetUnixTime(value) : null;
                    this.$set(this.valueOption, 'date', timestamp);
                }
            },
        },
        languageOptions() {
            return _.get(this.activeCms, 'languages') || [];
        },
        defaultLanguage() {
            return this.languageOptions.find((language) => language.default);
        },
        activeTranslation() {
            if (this.valueOption.translationOrigin) return this.valueOption;
        },
        maxLength() {
            return this.propType.value_field === 'text' ? undefined : 255;
        },
        uploadData() {
            let uploadData = {
                'context[type]': 'original',
            };
            return uploadData;
        },
        uploadEndpoint() {
            return `${this.setupStore.uploadEndpoint}/public`;
        },
        valueOptionGroupsFeatureEnabled() {
            const features = _.get(this.activeCms, 'config.template.features') || [];
            return features.includes('property-value-options:groups');
        },
        groups() {
            const valueOptions = this.property.valueOptions || [];
            return valueOptions.map((option) => option.group).filter(Boolean);
        },
        setupStore() {
            return useSetupStore();
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
    },
    watch: {
        // The drawer is reused for the next option, so "the user typed their own key"
        // has to stop applying the moment a different option is loaded into it.
        'valueOption.id': {
            handler() {
                this.keyTouched = false;
            },
            immediate: true,
        },
        // A key nobody typed follows the value while the option is new. Once it exists,
        // the key is frozen: every article and every import row points at it.
        value(value) {
            if (this.keyTouched || this.activeTranslation) return;
            if (!_.startsWith(_.get(this.valueOption, 'id'), 'new:')) return;

            this.$set(this.valueOption, 'key', this.availableKeyFor(value));

            // The rules are rebuilt as the value changes, which validates the key while
            // it is still empty. Re-check it now that it has been filled in, or the
            // "key is required" it just produced would stick.
            this.$nextTick(() => this.revalidateKey());
        },
    },
    methods: {
        // Values may repeat - two seasons of the same colour are two options - so the
        // suggested key has to step aside for the ones already taken instead of handing
        // the user a key the server will refuse. Mirrors PropertyValueOption::ensureKey().
        revalidateKey() {
            let parent = this.$parent;
            while (parent && typeof parent.validateField !== 'function') parent = parent.$parent;
            if (parent) parent.validateField('key', () => {});
        },
        availableKeyFor(value) {
            const derived = slugify(String(value || ''), { lower: true, strict: true });
            if (!derived) return '';

            const taken = (this.property.valueOptions || [])
                .filter((option) => option.id !== _.get(this.valueOption, 'id'))
                .map((option) => option.key);

            let key = derived;
            let counter = 1;
            while (taken.includes(key)) {
                counter++;
                key = `${derived}-${counter}`;
            }

            return key;
        },
        toggleTranslationOriginVisibility(key) {
            if (this.showTranslationOrigin.includes(key))
                this.showTranslationOrigin.splice(this.showTranslationOrigin.indexOf(key), 1);
            else this.showTranslationOrigin.push(key);
        },
        getLanguageName(code) {
            return language(code, this.locale ? this.locale.value : 'de');
        },
        getLanguageFlag(code) {
            return languageFlag(code);
        },
        getLanguageNativeName(code) {
            return languageNativeName(code);
        },
        findGroups(query, callback) {
            if (!this.groups.length) {
                callback([]);
                return false;
            }

            if (!query) {
                callback(this.groups.map((group) => ({ value: group })));
                return false;
            }

            const settings = {
                shouldSort: true,
                tokenize: true,
                matchAllTokens: true,
                threshold: 0.3,
                minMatchCharLength: 1,
                // keys: ['name'],
            };
            const fuse = new Fuse(this.groups, settings);
            const result = _.map(fuse.search(query), (el) => ({ value: el.item }));

            callback(result);
        },
        async updateUploadHeaders() {
            // The computed uploadHeaders reads the store token; make sure it is fresh.
            await ensureFreshToken();
        },
        async validateUpload(file) {
            await this.updateUploadHeaders();
            if (file.size / 1024 / 1024 > 2) {
                // this.$message.error('Avatar picture size can not exceed 2MB!');
                this.$notify.error({
                    title: this.$t('upload.sizeExceeded'),
                    message: this.$t('upload.max', {
                        size: '2mb',
                    }),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
                // Reject, never `return false`: an async before-upload always returns a promise,
                // and Element aborts only on rejection - a resolved `false` uploads anyway.
                return Promise.reject();
            }
        },
        async uploadSucceed(response, data) {
            let image = {
                id: uniqid('new:'),
                width: response.dimensions.width,
                height: response.dimensions.height,
                source: {
                    id: response.id,
                    name: response.name,
                    mime: response.mime,
                    type: 'original',
                    url: response.url,
                    upload: true,
                },
            };

            setTimeout(() => {
                if (this.$refs.upload) {
                    this.$refs.upload.uploadFiles = this.$refs.upload.uploadFiles.filter((el) => el.uid !== data.uid);
                }
            }, 1000);

            this.$set(this.valueOption, 'image', image);

            await this.updateUploadHeaders();
        },
        async uploadFailed(err, file) {
            await this.updateUploadHeaders();

            // console.log('>>>', err)
            // this.refreshToken('token')

            this.$notify.error({
                title: this.$t('upload.notUploaded'),
                message: _.get(file, 'name'),
                iconClass: 'fas el-icon-fa-xmark',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
        deleteImage() {
            // this.valueOption.image.deleted = true;
            this.$set(this.valueOption.image, 'deleted', true);
        },
        restoreImage() {
            // this.valueOption.image.deleted = false;
            this.$set(this.valueOption.image, 'deleted', false);
        },
        refreshToken(name) {
            return useAuthStore().refresh(name);
        },
    },
};
</script>

<style lang="sass" scoped>
.main-tab
    width: 100%
    display: inline-block
    position: relative
    // Explicit line-height: el-form-item__content sets it to the 36px control height,
    // which would space the hint out like a paragraph.
    .help
        display: block
        margin-top: 4px
        font-size: 12px
        line-height: 1.4
        color: var(--color-text-muted, #909399)
    // Element appends the error after the field's content and positions it absolutely
    // at the very bottom, which would drop it below the hint. Laying the content out
    // in flow lets the error sit against its input and the hint follow underneath.
    .value-option-key
        &::v-deep .el-form-item__content
            display: flex
            flex-direction: column
        &::v-deep .el-form-item__error
            position: static
            padding-top: 0
            margin-top: 4px
            order: 1
        .help
            order: 2
    .property-value-image
        display: inline-block
        position: relative
        & > img
            max-height: 180px
            max-width: 100%
            width: auto
            border-radius: 3px
            transition: .15s filter ease, .15s opacity ease
            background: $color-skeleton
        .el-button.delete-image,
        .el-button.restore-image
            position: absolute
            top: 5px
            right: 5px
            z-index: 2
            width: 30px
            height: 30px
            opacity: 0
            -webkit-transition: opacity .15s ease
            transition: opacity .15s ease
        &:hover
            .el-button.delete-image,
            .el-button.restore-image
                opacity: 1
        &.deleted
            & > img
                opacity: .6
                filter: grayscale(1) blur(1px)
</style>
